import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ButtonGroup } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";

type PeriodKey = "today" | "week" | "month" | "quarter" | "halfYear" | "year";

const PERIOD_KEYS: PeriodKey[] = ["today", "week", "month", "quarter", "halfYear", "year"];

function getPeriodRange(key: PeriodKey): RangeValue {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const date = now.getDate();

  switch (key) {
    case "today":
      return { start: new Date(year, month, date), end: new Date(year, month, date) };
    case "week": {
      const day = now.getDay();
      const mondayOffset = day === 0 ? 6 : day - 1;
      const monday = new Date(year, month, date - mondayOffset);
      const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
      return { start: monday, end: sunday };
    }
    case "month":
      return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
    case "quarter": {
      const qStart = Math.floor(month / 3) * 3;
      return { start: new Date(year, qStart, 1), end: new Date(year, qStart + 3, 0) };
    }
    case "halfYear": {
      const hStart = month < 6 ? 0 : 6;
      return { start: new Date(year, hStart, 1), end: new Date(year, hStart + 6, 0) };
    }
    case "year":
      return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
  }
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getActivePeriod(range: RangeValue): PeriodKey | null {
  for (const key of PERIOD_KEYS) {
    const expected = getPeriodRange(key);
    if (isSameDay(expected.start, range.start) && isSameDay(expected.end, range.end)) {
      return key;
    }
  }
  return null;
}

interface Props {
  value: RangeValue;
  onChange: (range: RangeValue) => void;
}

export default function PeriodPicker({ value, onChange }: Props) {
  const { t } = useTranslation("FinancePage");

  const options = useMemo(
    () =>
      PERIOD_KEYS.map(function buildOption(key) {
        return { value: key, label: t(`period.${key}`) };
      }),
    [t],
  );

  const activePeriod = getActivePeriod(value) ?? "";

  function handleChange(key: string) {
    onChange(getPeriodRange(key as PeriodKey));
  }

  return <ButtonGroup options={options} value={activePeriod} onChange={handleChange} mode={2} />;
}

